import type { ElementType, ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import type { ContainerBreakpoint, ContainerResponsiveValue } from "./contracts/responsive";
import { resolveContainerClasses } from "./foundations/responsiveClasses";
import { resolveGapClasses, type Space } from "./foundations/space";

type GridElement = "div" | "ul" | "ol";

export type GridProps<TElement extends GridElement = "div"> = {
  children: ReactNode;
  gap?: ContainerResponsiveValue<Space>;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

/** A twelve-column grid. GridItem owns each explicit span. */
export function Grid<TElement extends GridElement = "div">({
  as,
  children,
  gap,
  ref,
  ...nativeProps
}: GridProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element {...nativeProps} ref={ref} className={twJoin("grid grid-cols-12", resolveGapClasses(gap))}>
      {children}
    </Element>
  );
}

export type GridSpan = 3 | 4 | 6 | 8 | 9 | 12;

const GRID_SPAN_CLASSES: Record<GridSpan, string> = {
  3: "col-span-3",
  4: "col-span-4",
  6: "col-span-6",
  8: "col-span-8",
  9: "col-span-9",
  12: "col-span-12",
};

const GRID_CONTAINER_SPAN_CLASSES: Record<ContainerBreakpoint, Record<GridSpan, string>> = {
  xs: {
    3: "@xs:col-span-3",
    4: "@xs:col-span-4",
    6: "@xs:col-span-6",
    8: "@xs:col-span-8",
    9: "@xs:col-span-9",
    12: "@xs:col-span-12",
  },
  sm: {
    3: "@sm:col-span-3",
    4: "@sm:col-span-4",
    6: "@sm:col-span-6",
    8: "@sm:col-span-8",
    9: "@sm:col-span-9",
    12: "@sm:col-span-12",
  },
  md: {
    3: "@md:col-span-3",
    4: "@md:col-span-4",
    6: "@md:col-span-6",
    8: "@md:col-span-8",
    9: "@md:col-span-9",
    12: "@md:col-span-12",
  },
  lg: {
    3: "@lg:col-span-3",
    4: "@lg:col-span-4",
    6: "@lg:col-span-6",
    8: "@lg:col-span-8",
    9: "@lg:col-span-9",
    12: "@lg:col-span-12",
  },
  xl: {
    3: "@xl:col-span-3",
    4: "@xl:col-span-4",
    6: "@xl:col-span-6",
    8: "@xl:col-span-8",
    9: "@xl:col-span-9",
    12: "@xl:col-span-12",
  },
};

type GridItemElement = "div" | "li" | "section" | "article" | "aside";

export type GridItemProps<TElement extends GridItemElement = "div"> = {
  children: ReactNode;
  span: ContainerResponsiveValue<GridSpan>;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

export function GridItem<TElement extends GridItemElement = "div">({
  as,
  children,
  ref,
  span,
  ...nativeProps
}: GridItemProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={resolveContainerClasses(span, GRID_SPAN_CLASSES, GRID_CONTAINER_SPAN_CLASSES)}
    >
      {children}
    </Element>
  );
}
